import { FC, ReactNode, useRef, useEffect, useState } from 'react';
import { Box, InverseCorner, SanityImageProps } from '@mode/components';
import Image from 'gatsby-plugin-sanity-image';

type HeroImageProps = {
  image?: SanityImageProps;
  children?: ReactNode;
}

export const HeroImage: FC<HeroImageProps> = ({ image, children }) => {
  const topRef = useRef(null);
  const bottomRef = useRef(null);
  const cornerRef = useRef(null);
  const containerRef = useRef(null);
  const [bottomPos, setBottomPos] = useState({
    width: 0,
    height: 0,
    x: 0,
    y: 0,
  })
  const [topPos, setTopPos] = useState({
    width: 0,
    height: 0,
    x: 0,
    y: 0
  })
  const [cornerPos, setCornerPos] = useState({
    width: 0,
    height: 0,
    x: 0,
    y: 0
  })

  const handleResize = () => {
    if (typeof window === 'undefined' || !containerRef.current || !bottomRef.current || !topRef.current || !cornerRef.current) return null;
    const containerEl = containerRef.current.getBoundingClientRect()
    const topEl = topRef.current.getBoundingClientRect();
    const bottomEl = bottomRef.current.getBoundingClientRect();
    const cornerEl = cornerRef.current.getBoundingClientRect();

    setCornerPos({
      width: 100 / (cornerEl.width / containerEl.width),
      height: 100 / (cornerEl.height / containerEl.height),
      x: topEl.width * -1,
      y: (topEl.height - cornerEl.height) * -1,
    })

    setTopPos({
      width: 100 / (topEl.width / containerEl.width),
      height: 100 / (topEl.height / containerEl.height),
      x: 0,
      y: 0,
    })

    setBottomPos({
      width: 100 / (bottomEl.width / containerEl.width),
      height: 100 / (bottomEl.height / containerEl.height),
      x: (containerEl.width - bottomEl.width) * -1,
      y: (containerEl.height - bottomEl.height) * -1,
    })
  }

  useEffect(() => {
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    }
  }, [])

  return (
    <Box
      ref={containerRef}
      css={{
        position: 'relative',
        overflow: 'hidden',
        display: 'inline-block',
        width: '100%',
        height: '300px',
        top: '27.5%',
        mb: '$xxxs',
        left: 0,
        zIndex: 10,
        order: 1,
        background: 'transparent',
        '@md': {
          position: 'absolute',
          width: '57%',
          top: 'calc(10.5vw + 32px)',
          height: 'calc(100% - 25%)',
        },
        '@lg': {
          width: '58%',
          top: 'calc(10vw + 32px)',
          height: 'calc(100% - 28%)',
        },
        '@xl': {
          width: '56%',
          top: '27.5%',
          height: 'calc(100% - 27%)',
        }
      }}
    >
      {children}
      <Box
        ref={topRef}
        css={{
          position: 'absolute',
          top: 0,
          left: 0,
          height: '45%',
          width: '60%',
          backgroundColor: '$white',
          borderRadius: '$8',
          borderBottomLeftRadius: 0,
          borderBottomRightRadius: 0,
          background: 'transparent',
          '@md': {
            borderRadius: '$16',
            borderBottomLeftRadius: 0,
            borderBottomRightRadius: 0,
            width: '45%',
            height: '27%',
          },
          '@lg': {
            width: '45%'
          },
          '@xl': {
            width: '51.5%',
            height: '31%',
          }
        }}
      >
        <InverseCorner ref={cornerRef} position="bottomRight" bg="$white" css={{ overflow: 'hidden' }}>
          <Box css={{ position: 'absolute', lineHeight: 0, width: `${cornerPos.width}%`, height: `${cornerPos.height}%`, top: `${cornerPos.y}px`, left: `${cornerPos.x}px` }}>
            {image && <Image {...image} style={{ width: '100%', height: '100%', objectFit: 'cover' }} alt={image.alt ?? ''} />}
          </Box>
        </InverseCorner>
        <Box css={{
          position: 'absolute',
          width: '100%',
          height: '100%',
          overflow: 'hidden',
          borderRadius: '$8',
          borderBottomLeftRadius: 0,
          borderBottomRightRadius: 0,
          background: 'transparent',
          '@md': {
            borderRadius: '$16',
            borderBottomLeftRadius: 0,
            borderBottomRightRadius: 0,
          }
        }}>
          <Box
            css={{
              lineHeight: 0,
              position: 'absolute',
              overflow: 'hidden',
              width: `${topPos.width}%`,
              height: `${topPos.height}%`,
              left: `${topPos.x}px`,
              top: `${topPos.y}px`,
            }}>
            {image && <Image {...image} style={{ width: '100%', height: '100%', objectFit: 'cover' }} alt={image.alt ?? ''} />}
          </Box>
        </Box>
      </Box>
      <Box
        ref={bottomRef}
        css={{
          position: 'absolute',
          top: '45%',
          left: 0,
          height: 'calc(100% - 45%)',
          width: '100%',
          backgroundColor: '$white',
          borderRadius: '$8',
          borderTopLeftRadius: 0,
          overflow: 'hidden',
          lineHeight: 0,
          background: 'transparent',
          '@md': {
            height: 'calc(100% - 27%)',
            borderRadius: '$16',
            borderTopLeftRadius: 0,
            top: '27%',
          },
          '@xl': {
            height: 'calc(100% - 31%)',
            top: '31%',
          }
        }}
      >
        <Box css={{ position: 'absolute', lineHeight: 0, width: `${bottomPos.width}%`, height: `${bottomPos.height}%`, top: `${bottomPos.y}px`, left: `${bottomPos.x}px` }}>
          {image && <Image {...image} style={{ width: '100%', height: '100%', objectFit: 'cover' }} alt={image.alt ?? ''} />}
        </Box>
      </Box>
    </Box>
  )
}
